import { useState, useMemo } from 'react';
import type { Finanziamento } from '../../types';
import { formatCurrency } from '../../utils/pricing';
import {
  calculateLoanPayment,
  calculateTotalInterest,
  calculateTotalPaid,
} from '../../services/financeCalculations';

interface PrestitoFormProps {
  finanziamento?: Finanziamento;
  onSave: (data: Partial<Finanziamento>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// yyyy-mm-dd (input date) → ISO datetime UTC, oppure null
function dateToIso(value: string): string | null {
  return value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null;
}

export function PrestitoForm({
  finanziamento,
  onSave,
  onCancel,
  isLoading,
}: PrestitoFormProps) {
  const [formData, setFormData] = useState({
    nome: finanziamento?.nome || 'Prestito personale',
    importo: finanziamento?.importo || '',
    tassoInteresse: finanziamento
      ? String(parseFloat(finanziamento.tassoInteresse) * 100)
      : '6.5',
    durataAnni: finanziamento?.durataAnni || 5,
    rataMensile: finanziamento?.rataMensile || '',
    dataErogazione: finanziamento?.dataErogazione
      ? finanziamento.dataErogazione.split('T')[0]
      : '',
    dataPrimaRata: finanziamento?.dataPrimaRata
      ? finanziamento.dataPrimaRata.split('T')[0]
      : '',
    note: finanziamento?.note || '',
  });

  const calculations = useMemo(() => {
    const importo = parseFloat(formData.importo) || 0;
    const tasso = (parseFloat(formData.tassoInteresse) || 0) / 100;
    const durata = formData.durataAnni;

    const rataMensile = calculateLoanPayment(importo, tasso, durata);
    const totalePagato = calculateTotalPaid(rataMensile, durata);
    const totaleInteressi = calculateTotalInterest(importo, rataMensile, durata);

    return {
      rataMensile,
      totalePagato,
      totaleInteressi,
    };
  }, [formData.importo, formData.tassoInteresse, formData.durataAnni]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tassoDecimale = (parseFloat(formData.tassoInteresse) / 100).toFixed(4);
    // Rata effettiva: usa il valore inserito (da contratto), altrimenti quella calcolata
    const rataFinale =
      formData.rataMensile.trim() !== ''
        ? formData.rataMensile
        : calculations.rataMensile.toFixed(2);
    onSave({
      tipo: 'prestito',
      nome: formData.nome,
      importo: formData.importo,
      tassoInteresse: tassoDecimale,
      durataAnni: formData.durataAnni,
      rataMensile: rataFinale,
      dataErogazione: dateToIso(formData.dataErogazione),
      dataPrimaRata: dateToIso(formData.dataPrimaRata),
      note: formData.note || null,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'durataAnni' ? parseInt(value, 10) : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="input-group">
        <label htmlFor="nome" className="input-label">
          Nome Prestito
        </label>
        <input
          type="text"
          id="nome"
          name="nome"
          value={formData.nome}
          onChange={handleChange}
          className="w-full"
          placeholder="es. Prestito personale FinanziariaXYZ"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="input-group">
          <label htmlFor="importo" className="input-label">
            Importo Richiesto (€)
          </label>
          <input
            type="number"
            id="importo"
            name="importo"
            value={formData.importo}
            onChange={handleChange}
            className="w-full"
            min="0"
            step="500"
            placeholder="15000"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="durataAnni" className="input-label">
            Durata (anni)
          </label>
          <select
            id="durataAnni"
            name="durataAnni"
            value={formData.durataAnni}
            onChange={handleChange}
            className="w-full"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 10].map((anni) => (
              <option key={anni} value={anni}>
                {anni} {anni === 1 ? 'anno' : 'anni'}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="input-group">
        <label htmlFor="tassoInteresse" className="input-label">
          Tasso di Interesse Annuo (TAN) %
        </label>
        <div className="relative">
          <input
            type="number"
            id="tassoInteresse"
            name="tassoInteresse"
            value={formData.tassoInteresse}
            onChange={handleChange}
            className="w-full pr-8"
            min="0"
            max="30"
            step="0.1"
            placeholder="6.5"
            required
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
            %
          </span>
        </div>
      </div>

      {/* Date e rata effettiva */}
      <div className="grid grid-cols-2 gap-4">
        <div className="input-group">
          <label htmlFor="dataErogazione" className="input-label">
            Data erogazione
          </label>
          <input
            type="date"
            id="dataErogazione"
            name="dataErogazione"
            value={formData.dataErogazione}
            onChange={handleChange}
            className="w-full"
          />
        </div>
        <div className="input-group">
          <label htmlFor="dataPrimaRata" className="input-label">
            Data prima rata
          </label>
          <input
            type="date"
            id="dataPrimaRata"
            name="dataPrimaRata"
            value={formData.dataPrimaRata}
            onChange={handleChange}
            className="w-full"
          />
        </div>
      </div>

      <div className="input-group">
        <label htmlFor="rataMensile" className="input-label">
          Rata mensile effettiva (€) — opzionale
        </label>
        <input
          type="number"
          id="rataMensile"
          name="rataMensile"
          value={formData.rataMensile}
          onChange={handleChange}
          className="w-full"
          min="0"
          step="0.01"
          placeholder={calculations.rataMensile.toFixed(2)}
        />
        <p className="mt-1 text-xs text-gray-500">
          Lascia vuoto per usare la rata calcolata ({formatCurrency(calculations.rataMensile)}).
          Inserisci il valore di contratto se differisce.
        </p>
      </div>

      {/* Calculation Preview */}
      <div className="bg-success-50 border border-success-200 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-success-800">Riepilogo Calcoli</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-success-600">Rata Mensile</p>
            <p className="text-lg font-bold text-success-800 currency">
              {formatCurrency(calculations.rataMensile)}
            </p>
          </div>
          <div>
            <p className="text-xs text-success-600">Totale Interessi</p>
            <p className="text-lg font-bold text-warning-700 currency">
              {formatCurrency(calculations.totaleInteressi)}
            </p>
          </div>
          <div>
            <p className="text-xs text-success-600">Totale da Pagare</p>
            <p className="text-lg font-bold text-gray-900 currency">
              {formatCurrency(calculations.totalePagato)}
            </p>
          </div>
        </div>
      </div>

      <div className="input-group">
        <label htmlFor="note" className="input-label">
          Note (opzionale)
        </label>
        <textarea
          id="note"
          name="note"
          value={formData.note}
          onChange={handleChange}
          className="w-full"
          rows={2}
          placeholder="es. Finalità ristrutturazione, TAEG 7.2%"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary flex-1"
          disabled={isLoading}
        >
          Annulla
        </button>
        <button
          type="submit"
          className="btn-primary flex-1"
          disabled={isLoading}
        >
          {isLoading ? 'Salvataggio...' : finanziamento ? 'Salva Modifiche' : 'Aggiungi Prestito'}
        </button>
      </div>
    </form>
  );
}
